import { useState, useEffect } from "react";

/* ─── CONFIG ─────────────────────────────────────────────────────────────── */
// Change this to your cPanel domain after deployment
const API = "/api";
const CUR = "₦";

/* ─── HELPERS ────────────────────────────────────────────────────────────── */
const money = (n) =>
  `${CUR}${(+n || 0).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n) => `${(+n || 0).toFixed(1)}%`;
const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStart = () =>
  new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

async function req(path, method = "GET", body = null) {
  try {
    const r = await fetch(`${API}/${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    return await r.json();
  } catch (e) {
    return { error: String(e) };
  }
}

/* ─── SHARED UI ──────────────────────────────────────────────────────────── */

function Modal({ title, onClose, children }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.75)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: "#0c1b0e", border: "1px solid #1e3520", borderRadius: 14, width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #152817", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>{title}</span>
          <button onClick={onClose} style={{ color: "#555", fontSize: 22, background: "none", border: "none", cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", color: "#666", fontSize: 11, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1.2 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", background: "#07120a", border: "1px solid #1e3520", borderRadius: 8,
  padding: "10px 14px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box",
  fontFamily: "inherit",
};

function Inp({ label, ...props }) {
  return (
    <Field label={label}>
      <input {...props} style={{ ...inputStyle, ...props.style }} />
    </Field>
  );
}

function Sel({ label, children, ...props }) {
  return (
    <Field label={label}>
      <select {...props} style={{ ...inputStyle, ...props.style }}>{children}</select>
    </Field>
  );
}

function Btn({ children, variant = "primary", ...props }) {
  const v = {
    primary: { background: "#d4a017", color: "#000", fontWeight: 700, border: "none" },
    danger:  { background: "#ef4444", color: "#fff", fontWeight: 600, border: "none" },
    ghost:   { background: "transparent", color: "#888", border: "1px solid #1e3520" },
    amber:   { background: "#f59e0b", color: "#000", fontWeight: 700, border: "none" },
  };
  return (
    <button {...props} style={{ padding: "9px 18px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontFamily: "inherit", ...v[variant], ...props.style }}>
      {children}
    </button>
  );
}

function Card({ label, value, sub, color = "#d4a017" }) {
  return (
    <div style={{ background: "#0c1b0e", border: "1px solid #163318", borderRadius: 12, padding: "18px 22px" }}>
      <div style={{ color: "#555", fontSize: 10, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10 }}>{label}</div>
      <div style={{ color: "#fff", fontSize: 24, fontWeight: 800, fontFamily: "monospace", letterSpacing: -0.5 }}>{value ?? "—"}</div>
      {sub && <div style={{ color, fontSize: 12, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

function Table({ cols, rows, empty = "No data yet" }) {
  return (
    <div style={{ background: "#0c1b0e", border: "1px solid #163318", borderRadius: 12, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #152817" }}>
            {cols.map((c, i) => (
              <th key={i} style={{ padding: "11px 16px", textAlign: "left", color: "#444", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8 }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={cols.length} style={{ padding: 32, textAlign: "center", color: "#3a3a3a" }}>{empty}</td></tr>
          ) : rows}
        </tbody>
      </table>
    </div>
  );
}

const TR = ({ children, style }) => (
  <tr style={{ borderBottom: "1px solid #112214", ...style }}>{children}</tr>
);
const TD = ({ children, style }) => (
  <td style={{ padding: "11px 16px", color: "#ccc", fontSize: 13, ...style }}>{children}</td>
);

function MarginBadge({ value }) {
  const v = +value;
  const [bg, fg] = v >= 30 ? ["#2a1e02","#d4a017"] : v >= 15 ? ["#2d1f02","#f59e0b"] : ["#2d0505","#ef4444"];
  return <span style={{ background: bg, color: fg, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontFamily: "monospace" }}>{pct(v)}</span>;
}

/* ─── SCREEN: DASHBOARD ──────────────────────────────────────────────────── */
function Dashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    req(`reports.php?type=summary&from=${todayStr()}&to=${todayStr()}`).then(setStats);
    req("sales.php?limit=8").then((d) => Array.isArray(d) && setOrders(d));
  }, []);

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: -0.5 }}>Dashboard</h1>
        <div style={{ color: "#444", fontSize: 13, marginTop: 2 }}>Today — {new Date().toDateString()}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginBottom: 28 }}>
        <Card label="Today's Revenue" value={money(stats?.total_revenue)} sub={`${stats?.total_orders ?? 0} orders`} />
        <Card label="Today's Profit"  value={money(stats?.total_profit)}  sub={stats ? `margin ${pct(stats.avg_margin)}` : null} />
        <Card label="Avg Margin"      value={pct(stats?.avg_margin)}       color="#f59e0b" />
        <Card label="Best Margin"     value={pct(stats?.best_margin)}      color="#8b5cf6" />
      </div>

      <Table
        cols={["Order #", "Total", "Profit", "Margin", "Payment", "Time"]}
        empty="No orders yet today"
        rows={orders.map((o) => (
          <TR key={o.id}>
            <TD style={{ color: "#d4a017", fontFamily: "monospace", fontSize: 12 }}>{o.order_number}</TD>
            <TD style={{ fontFamily: "monospace" }}>{money(o.total_amount)}</TD>
            <TD style={{ color: "#d4a017", fontFamily: "monospace" }}>{money(o.profit)}</TD>
            <TD><MarginBadge value={o.profit_margin} /></TD>
            <TD style={{ color: "#888", textTransform: "capitalize" }}>{o.payment_method}</TD>
            <TD style={{ color: "#444", fontSize: 12 }}>{new Date(o.created_at).toLocaleTimeString()}</TD>
          </TR>
        ))}
      />
    </div>
  );
}

/* ─── SCREEN: PRODUCTS ───────────────────────────────────────────────────── */
function Products() {
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", sku: "", selling_price: "", category: "" });

  const load = () => req("products.php").then((d) => Array.isArray(d) && setProducts(d));
  useEffect(() => { load(); }, []);

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openAdd = () => { setForm({ name: "", sku: "", selling_price: "", category: "" }); setModal("add"); };
  const openEdit = (p) => { setForm(p); setModal(p); };

  const save = async () => {
    modal === "add"
      ? await req("products.php", "POST", form)
      : await req(`products.php?id=${modal.id}`, "PUT", form);
    setModal(null); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await req(`products.php?id=${id}`, "DELETE"); load();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: -0.5 }}>Products</h1>
        <Btn onClick={openAdd}>+ Add Product</Btn>
      </div>

      <Table
        cols={["Product", "SKU", "Sell Price", "BOM Cost", "Profit", "Margin", ""]}
        rows={products.map((p) => (
          <TR key={p.id}>
            <TD style={{ color: "#fff", fontWeight: 600 }}>{p.name}</TD>
            <TD style={{ color: "#444", fontFamily: "monospace", fontSize: 12 }}>{p.sku || "—"}</TD>
            <TD style={{ fontFamily: "monospace" }}>{money(p.selling_price)}</TD>
            <TD style={{ color: "#f59e0b", fontFamily: "monospace" }}>{money(p.bom_cost)}</TD>
            <TD style={{ color: "#d4a017", fontFamily: "monospace" }}>{money(p.profit)}</TD>
            <TD><MarginBadge value={p.margin} /></TD>
            <TD>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="ghost" style={{ padding: "4px 12px" }} onClick={() => openEdit(p)}>Edit</Btn>
                <Btn variant="danger" style={{ padding: "4px 12px" }} onClick={() => del(p.id)}>Del</Btn>
              </div>
            </TD>
          </TR>
        ))}
      />

      {modal && (
        <Modal title={modal === "add" ? "New Product" : "Edit Product"} onClose={() => setModal(null)}>
          <Inp label="Product Name *" value={form.name} onChange={f("name")} placeholder="e.g. Jollof Rice" />
          <Inp label="SKU" value={form.sku || ""} onChange={f("sku")} placeholder="e.g. JR-001" />
          <Inp label={`Selling Price (${CUR}) *`} type="number" value={form.selling_price} onChange={f("selling_price")} placeholder="0.00" />
          <Inp label="Category" value={form.category || ""} onChange={f("category")} placeholder="e.g. Food" />
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={save}>Save Product</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── SCREEN: COMPONENTS ─────────────────────────────────────────────────── */
function Components() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", unit: "unit", unit_cost: "", stock_qty: "0" });
  const UNITS = ["unit", "kg", "g", "litre", "ml", "pcs", "bag", "bottle", "pack", "box", "sachet"];

  const load = () => req("components.php").then((d) => Array.isArray(d) && setItems(d));
  useEffect(() => { load(); }, []);

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openAdd = () => { setForm({ name: "", unit: "unit", unit_cost: "", stock_qty: "0" }); setModal("add"); };
  const openEdit = (c) => { setForm(c); setModal(c); };

  const save = async () => {
    modal === "add"
      ? await req("components.php", "POST", form)
      : await req(`components.php?id=${modal.id}`, "PUT", form);
    setModal(null); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this component?")) return;
    await req(`components.php?id=${id}`, "DELETE"); load();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: -0.5 }}>Components</h1>
        <Btn onClick={openAdd}>+ Add Component</Btn>
      </div>

      <Table
        cols={["Name", "Unit", "Cost / Unit", "Stock Qty", ""]}
        rows={items.map((c) => (
          <TR key={c.id}>
            <TD style={{ color: "#fff", fontWeight: 600 }}>{c.name}</TD>
            <TD style={{ color: "#666" }}>{c.unit}</TD>
            <TD style={{ color: "#f59e0b", fontFamily: "monospace" }}>{money(c.unit_cost)}</TD>
            <TD style={{ fontFamily: "monospace", color: "#888" }}>{c.stock_qty}</TD>
            <TD>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="ghost" style={{ padding: "4px 12px" }} onClick={() => openEdit(c)}>Edit</Btn>
                <Btn variant="danger" style={{ padding: "4px 12px" }} onClick={() => del(c.id)}>Del</Btn>
              </div>
            </TD>
          </TR>
        ))}
      />

      {modal && (
        <Modal title={modal === "add" ? "New Component" : "Edit Component"} onClose={() => setModal(null)}>
          <Inp label="Name *" value={form.name} onChange={f("name")} placeholder="e.g. Rice" />
          <Sel label="Unit *" value={form.unit} onChange={f("unit")}>
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </Sel>
          <Inp label={`Cost per Unit (${CUR}) *`} type="number" value={form.unit_cost} onChange={f("unit_cost")} placeholder="0.00" />
          <Inp label="Stock Qty" type="number" value={form.stock_qty} onChange={f("stock_qty")} placeholder="0" />
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── SCREEN: BOM ────────────────────────────────────────────────────────── */
function BOM() {
  const [products, setProducts] = useState([]);
  const [components, setComponents] = useState([]);
  const [pid, setPid] = useState("");
  const [lines, setLines] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [addForm, setAddForm] = useState({ component_id: "", quantity: 1 });

  useEffect(() => {
    req("products.php").then((d) => Array.isArray(d) && setProducts(d));
    req("components.php").then((d) => Array.isArray(d) && setComponents(d));
  }, []);

  const loadBOM = (id) => req(`bom.php?product_id=${id}`).then((d) => { setLines(d.bom || []); setTotalCost(d.total_bom_cost || 0); });

  const selectProduct = (id) => { setPid(id); if (id) loadBOM(id); else { setLines([]); setTotalCost(0); } };

  const addLine = async () => {
    if (!addForm.component_id || !pid) return;
    await req("bom.php", "POST", { product_id: +pid, component_id: +addForm.component_id, quantity: +addForm.quantity });
    setAddForm({ component_id: "", quantity: 1 });
    loadBOM(pid);
  };

  const removeLine = async (id) => { await req(`bom.php?id=${id}`, "DELETE"); loadBOM(pid); };
  const updateQty = async (id, qty) => { await req(`bom.php?id=${id}`, "PUT", { quantity: +qty }); loadBOM(pid); };

  const selected = products.find((p) => p.id === +pid);

  return (
    <div>
      <h1 style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: -0.5, marginBottom: 24 }}>Bill of Materials</h1>

      <div style={{ maxWidth: 400, marginBottom: 24 }}>
        <Sel label="Select Product" value={pid} onChange={(e) => selectProduct(e.target.value)}>
          <option value="">— Choose a product —</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Sel>
      </div>

      {pid && selected && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 24 }}>
            <Card label="Selling Price"    value={money(selected.selling_price)} />
            <Card label="Total BOM Cost"   value={money(totalCost)} color="#f59e0b" />
            <Card label="Gross Profit"     value={money(selected.selling_price - totalCost)}
              color={(selected.selling_price - totalCost) >= 0 ? "#d4a017" : "#ef4444"} />
          </div>

          <Table
            cols={["Component", "Unit", "Qty Used", "Unit Cost", "Line Cost", ""]}
            empty="No components — add below"
            rows={lines.map((b) => (
              <TR key={b.id}>
                <TD style={{ color: "#fff" }}>{b.component_name}</TD>
                <TD style={{ color: "#666" }}>{b.unit}</TD>
                <TD>
                  <input type="number" defaultValue={b.quantity}
                    onBlur={(e) => updateQty(b.id, e.target.value)}
                    style={{ width: 72, background: "#07120a", border: "1px solid #1e3520", borderRadius: 6, padding: "5px 9px", color: "#fff", fontFamily: "monospace", outline: "none" }} />
                </TD>
                <TD style={{ color: "#f59e0b", fontFamily: "monospace" }}>{money(b.unit_cost)}</TD>
                <TD style={{ color: "#fff", fontFamily: "monospace" }}>{money(b.line_cost)}</TD>
                <TD><Btn variant="danger" style={{ padding: "4px 12px" }} onClick={() => removeLine(b.id)}>Remove</Btn></TD>
              </TR>
            ))}
          />

          <div style={{ background: "#0c1b0e", border: "1px solid #163318", borderRadius: 12, padding: 20, marginTop: 16 }}>
            <div style={{ color: "#555", fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 16 }}>Add Component to BOM</div>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: 2, minWidth: 200 }}>
                <Sel label="Component" value={addForm.component_id} onChange={(e) => setAddForm({ ...addForm, component_id: e.target.value })}>
                  <option value="">— Select —</option>
                  {components.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.unit}) — {money(c.unit_cost)}</option>)}
                </Sel>
              </div>
              <div style={{ flex: 1, minWidth: 100 }}>
                <Inp label="Quantity" type="number" value={addForm.quantity} onChange={(e) => setAddForm({ ...addForm, quantity: e.target.value })} />
              </div>
              <Btn onClick={addLine} style={{ marginBottom: 16 }}>Add Line</Btn>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── SCREEN: POS ────────────────────────────────────────────────────────── */
function POS() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [payment, setPayment] = useState("cash");
  const [note, setNote] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => { req("products.php").then((d) => Array.isArray(d) && setProducts(d)); }, []);

  const addToCart = (p) =>
    setCart((prev) => {
      const ex = prev.find((i) => i.product_id === p.id);
      return ex
        ? prev.map((i) => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i)
        : [...prev, { product_id: p.id, name: p.name, price: +p.selling_price, qty: 1 }];
    });

  const updateQty = (pid, qty) => qty < 1 ? removeItem(pid) : setCart((p) => p.map((i) => i.product_id === pid ? { ...i, qty } : i));
  const removeItem = (pid) => setCart((p) => p.filter((i) => i.product_id !== pid));

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const total = Math.max(subtotal - +discount, 0);

  const checkout = async () => {
    if (!cart.length) return;
    const res = await req("sales.php", "POST", {
      items: cart.map((i) => ({ product_id: i.product_id, quantity: i.qty })),
      discount: +discount, payment_method: payment, note,
    });
    if (res.error) { alert("Error: " + res.error); return; }
    setReceipt(res);
    setCart([]); setDiscount(0); setNote("");
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20, height: "calc(100vh - 80px)" }}>
      {/* Products */}
      <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍  Search products..."
          style={{ ...inputStyle, marginBottom: 16, fontSize: 14, padding: "12px 16px" }} />
        <div style={{ flex: 1, overflowY: "auto", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12, alignContent: "start" }}>
          {filtered.map((p) => (
            <div key={p.id} onClick={() => addToCart(p)}
              style={{ background: "#0c1b0e", border: "1px solid #163318", borderRadius: 12, padding: 16, cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#d4a017"; e.currentTarget.style.background = "#0e2212"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#163318"; e.currentTarget.style.background = "#0c1b0e"; }}>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 13, marginBottom: 6, lineHeight: 1.3 }}>{p.name}</div>
              <div style={{ color: "#d4a017", fontFamily: "monospace", fontSize: 15, fontWeight: 700 }}>{money(p.selling_price)}</div>
              <div style={{ color: "#444", fontSize: 10, marginTop: 4 }}>margin {pct(p.margin)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Cart */}
      <div style={{ background: "#0c1b0e", border: "1px solid #163318", borderRadius: 14, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #152817", color: "#555", fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2 }}>
          Cart · {cart.length} items
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {cart.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "#333", fontSize: 13 }}>Tap a product to add</div>
          ) : cart.map((item) => (
            <div key={item.product_id} style={{ padding: "10px 16px", borderBottom: "1px solid #112214", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "#fff", fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                <div style={{ color: "#d4a017", fontFamily: "monospace", fontSize: 12 }}>{money(item.price * item.qty)}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <button onClick={() => updateQty(item.product_id, item.qty - 1)} style={{ background: "#0a1a0c", border: "1px solid #1e3520", borderRadius: 6, color: "#fff", width: 26, height: 26, cursor: "pointer", fontSize: 16, lineHeight: 1 }}>−</button>
                <span style={{ color: "#fff", fontFamily: "monospace", minWidth: 18, textAlign: "center", fontSize: 13 }}>{item.qty}</span>
                <button onClick={() => updateQty(item.product_id, item.qty + 1)} style={{ background: "#0a1a0c", border: "1px solid #1e3520", borderRadius: 6, color: "#fff", width: 26, height: 26, cursor: "pointer", fontSize: 16, lineHeight: 1 }}>+</button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: "14px 16px", borderTop: "1px solid #152817" }}>
          {[["Subtotal", money(subtotal), "#ccc"]].map(([l, v, c]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ color: "#666", fontSize: 13 }}>{l}</span>
              <span style={{ color: c, fontFamily: "monospace", fontSize: 13 }}>{v}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ color: "#666", fontSize: 13 }}>Discount</span>
            <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)}
              style={{ width: 90, background: "#07120a", border: "1px solid #1e3520", borderRadius: 6, padding: "5px 10px", color: "#f59e0b", fontFamily: "monospace", textAlign: "right", outline: "none" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>Total</span>
            <span style={{ color: "#d4a017", fontFamily: "monospace", fontSize: 18, fontWeight: 800 }}>{money(total)}</span>
          </div>
          <select value={payment} onChange={(e) => setPayment(e.target.value)}
            style={{ ...inputStyle, marginBottom: 10, fontSize: 13 }}>
            <option value="cash">💵 Cash</option>
            <option value="transfer">📲 Transfer</option>
            <option value="card">💳 Card</option>
            <option value="pos">🖥 POS Terminal</option>
          </select>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
            style={{ ...inputStyle, marginBottom: 12, fontSize: 13 }} />
          <Btn onClick={checkout} style={{ width: "100%", padding: 13, fontSize: 14, opacity: cart.length ? 1 : 0.4 }}>
            Checkout
          </Btn>
        </div>
      </div>

      {receipt && (
        <Modal title="✓ Order Complete" onClose={() => setReceipt(null)}>
          <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
            <div style={{ color: "#d4a017", fontSize: 44, marginBottom: 6 }}>✓</div>
            <div style={{ color: "#555", fontFamily: "monospace", fontSize: 13 }}>{receipt.order_number}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
            {[["TOTAL",   money(receipt.total_amount), "#fff"],
              ["PROFIT",  money(receipt.profit),       "#d4a017"],
              ["COST",    money(receipt.total_cost),   "#f59e0b"],
              ["MARGIN",  pct(receipt.margin),         "#8b5cf6"]].map(([l, v, c]) => (
              <div key={l} style={{ background: "#07120a", borderRadius: 10, padding: 14 }}>
                <div style={{ color: "#444", fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>{l}</div>
                <div style={{ color: c, fontFamily: "monospace", fontWeight: 700, fontSize: 16 }}>{v}</div>
              </div>
            ))}
          </div>
          <Btn onClick={() => setReceipt(null)} style={{ width: "100%", padding: 13 }}>New Sale</Btn>
        </Modal>
      )}
    </div>
  );
}

/* ─── SCREEN: REPORTS ────────────────────────────────────────────────────── */
function Reports() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [margins, setMargins] = useState([]);

  const load = () => {
    const q = `from=${from}&to=${to}`;
    req(`reports.php?type=summary&${q}`).then(setSummary);
    req(`reports.php?type=daily&${q}`).then((d) => Array.isArray(d) && setDaily(d));
    req(`reports.php?type=top_products&${q}`).then((d) => Array.isArray(d) && setTopProducts(d));
    req("reports.php?type=margins").then((d) => Array.isArray(d) && setMargins(d));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: -0.5 }}>Reports</h1>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            style={{ background: "#0c1b0e", border: "1px solid #1e3520", borderRadius: 8, padding: "8px 12px", color: "#fff", outline: "none" }} />
          <span style={{ color: "#444" }}>→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            style={{ background: "#0c1b0e", border: "1px solid #1e3520", borderRadius: 8, padding: "8px 12px", color: "#fff", outline: "none" }} />
          <Btn onClick={load}>Apply</Btn>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 28 }}>
        <Card label="Revenue"     value={money(summary?.total_revenue)} sub={`${summary?.total_orders ?? 0} orders`} />
        <Card label="Total Cost"  value={money(summary?.total_cost)}    color="#f59e0b" />
        <Card label="Gross Profit"value={money(summary?.total_profit)}  color="#d4a017" />
        <Card label="Avg Margin"  value={pct(summary?.avg_margin)}      color="#8b5cf6" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <Table
          cols={["Date", "Orders", "Revenue", "Profit"]}
          empty="No data in range"
          rows={daily.map((d) => (
            <TR key={d.sale_date}>
              <TD style={{ color: "#666", fontFamily: "monospace", fontSize: 12 }}>{d.sale_date}</TD>
              <TD style={{ color: "#888" }}>{d.orders}</TD>
              <TD style={{ fontFamily: "monospace", fontSize: 12 }}>{money(d.revenue)}</TD>
              <TD style={{ color: "#d4a017", fontFamily: "monospace", fontSize: 12 }}>{money(d.profit)}</TD>
            </TR>
          ))}
        />

        <Table
          cols={["Product", "Qty", "Revenue", "Margin"]}
          empty="No sales in range"
          rows={topProducts.map((p) => (
            <TR key={p.product_name}>
              <TD style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>{p.product_name}</TD>
              <TD style={{ color: "#888" }}>{p.total_qty_sold}</TD>
              <TD style={{ fontFamily: "monospace", fontSize: 12 }}>{money(p.total_revenue)}</TD>
              <TD><MarginBadge value={p.margin} /></TD>
            </TR>
          ))}
        />
      </div>

      <Table
        cols={["Product", "Sell Price", "BOM Cost", "Profit", "Margin %"]}
        empty="No products"
        rows={margins.map((p) => (
          <TR key={p.id}>
            <TD style={{ color: "#fff", fontWeight: 600 }}>{p.name}</TD>
            <TD style={{ fontFamily: "monospace" }}>{money(p.selling_price)}</TD>
            <TD style={{ color: "#f59e0b", fontFamily: "monospace" }}>{money(p.bom_cost)}</TD>
            <TD style={{ color: "#d4a017", fontFamily: "monospace" }}>{money(p.profit)}</TD>
            <TD>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 80, height: 5, background: "#112214", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(Math.max(+p.margin, 0), 100)}%`, height: "100%", background: +p.margin >= 30 ? "#d4a017" : +p.margin >= 15 ? "#f59e0b" : "#ef4444", borderRadius: 3 }} />
                </div>
                <MarginBadge value={p.margin} />
              </div>
            </TD>
          </TR>
        ))}
      />
    </div>
  );
}

/* ─── SCREEN: EXPENSES ───────────────────────────────────────────────────── */
function Expenses() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ title: "", amount: "", category: "General", note: "", expense_date: todayStr() });
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());
  const CATS = ["General", "Supplies", "Utilities", "Salaries", "Rent", "Marketing", "Transport", "Maintenance", "Other"];

  const load = () => req(`expenses.php?from=${from}&to=${to}`).then((d) => Array.isArray(d) && setItems(d));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openAdd = () => { setForm({ title: "", amount: "", category: "General", note: "", expense_date: todayStr() }); setModal("add"); };
  const openEdit = (e) => { setForm(e); setModal(e); };

  const save = async () => {
    modal === "add"
      ? await req("expenses.php", "POST", form)
      : await req(`expenses.php?id=${modal.id}`, "PUT", form);
    setModal(null); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete expense?")) return;
    await req(`expenses.php?id=${id}`, "DELETE"); load();
  };

  const total = items.reduce((s, e) => s + +e.amount, 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: -0.5 }}>Expenses</h1>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            style={{ background: "#0c1b0e", border: "1px solid #1e3520", borderRadius: 8, padding: "8px 12px", color: "#fff", outline: "none" }} />
          <span style={{ color: "#444" }}>→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            style={{ background: "#0c1b0e", border: "1px solid #1e3520", borderRadius: 8, padding: "8px 12px", color: "#fff", outline: "none" }} />
          <Btn variant="ghost" onClick={load}>Filter</Btn>
          <Btn onClick={openAdd}>+ Add Expense</Btn>
        </div>
      </div>

      <div style={{ background: "#0c1b0e", border: "1px solid #ef4444", borderRadius: 12, padding: "14px 22px", marginBottom: 20, display: "inline-flex", alignItems: "center", gap: 14 }}>
        <span style={{ color: "#666", fontSize: 13 }}>Total Expenses</span>
        <span style={{ color: "#ef4444", fontFamily: "monospace", fontSize: 22, fontWeight: 800 }}>{money(total)}</span>
      </div>

      <Table
        cols={["Date", "Title", "Category", "Amount", "Note", ""]}
        empty="No expenses recorded"
        rows={items.map((e) => (
          <TR key={e.id}>
            <TD style={{ color: "#666", fontFamily: "monospace", fontSize: 12 }}>{e.expense_date}</TD>
            <TD style={{ color: "#fff", fontWeight: 600 }}>{e.title}</TD>
            <TD>
              <span style={{ background: "#1a1230", color: "#8b5cf6", padding: "3px 10px", borderRadius: 20, fontSize: 11 }}>{e.category}</span>
            </TD>
            <TD style={{ color: "#ef4444", fontFamily: "monospace" }}>{money(e.amount)}</TD>
            <TD style={{ color: "#444", fontSize: 12 }}>{e.note || "—"}</TD>
            <TD>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="ghost" style={{ padding: "4px 12px" }} onClick={() => openEdit(e)}>Edit</Btn>
                <Btn variant="danger" style={{ padding: "4px 12px" }} onClick={() => del(e.id)}>Del</Btn>
              </div>
            </TD>
          </TR>
        ))}
      />

      {modal && (
        <Modal title={modal === "add" ? "New Expense" : "Edit Expense"} onClose={() => setModal(null)}>
          <Inp label="Title *" value={form.title} onChange={f("title")} placeholder="e.g. Electricity bill" />
          <Inp label={`Amount (${CUR}) *`} type="number" value={form.amount} onChange={f("amount")} placeholder="0.00" />
          <Sel label="Category" value={form.category} onChange={f("category")}>
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </Sel>
          <Inp label="Date *" type="date" value={form.expense_date} onChange={f("expense_date")} />
          <Field label="Note (optional)">
            <textarea value={form.note || ""} onChange={f("note")}
              style={{ ...inputStyle, resize: "vertical", minHeight: 72 }} />
          </Field>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={save}>Save Expense</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── NAV CONFIG ─────────────────────────────────────────────────────────── */
const NAV = [
  { id: "dashboard",  label: "Dashboard",   icon: "▦" },
  { id: "pos",        label: "POS",          icon: "⊕" },
  { id: "products",   label: "Products",     icon: "▤" },
  { id: "components", label: "Components",   icon: "◈" },
  { id: "bom",        label: "BOM",          icon: "◑" },
  { id: "reports",    label: "Reports",      icon: "◱" },
  { id: "expenses",   label: "Expenses",     icon: "◻" },
];

const SCREENS = { dashboard: Dashboard, pos: POS, products: Products, components: Components, bom: BOM, reports: Reports, expenses: Expenses };

/* ─── ROOT APP ───────────────────────────────────────────────────────────── */
export default function App() {
  const [screen, setScreen] = useState("dashboard");
  const Screen = SCREENS[screen];

  return (
    <div style={{ display: "flex", height: "100vh", background: "#07120a", color: "#fff", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&display=swap');
        * { box-sizing: border-box; font-family: 'Syne', sans-serif; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: #07120a; }
        ::-webkit-scrollbar-thumb { background: #1e3520; border-radius: 2px; }
        input[type=date]::-webkit-calendar-picker-indicator { filter: invert(0.4); cursor: pointer; }
        input:focus, select:focus, textarea:focus { border-color: #d4a017 !important; }
        button:active { opacity: 0.85; }
      `}</style>

      {/* Sidebar */}
      <aside style={{ width: 210, background: "#050d06", borderRight: "1px solid #112214", display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ padding: "22px 20px 18px", borderBottom: "1px solid #112214" }}>
          <div style={{ color: "#d4a017", fontWeight: 800, fontSize: 21, letterSpacing: 1 }}>ABIDAR</div>
          <div style={{ color: "#2a4a2c", fontSize: 9, marginTop: 3, letterSpacing: 2 }}>SALES · COSTING · PROFIT</div>
        </div>

        <nav style={{ flex: 1, padding: "10px 0" }}>
          {NAV.map(({ id, label, icon }) => (
            <button key={id} onClick={() => setScreen(id)} style={{
              display: "flex", alignItems: "center", gap: 11, width: "100%",
              padding: "10px 20px", background: screen === id ? "#112214" : "transparent",
              color: screen === id ? "#fff" : "#4a4a4a",
              border: "none", borderLeft: `3px solid ${screen === id ? "#d4a017" : "transparent"}`,
              cursor: "pointer", textAlign: "left", fontSize: 13,
              fontWeight: screen === id ? 700 : 400, transition: "all 0.1s", fontFamily: "inherit",
            }}
            onMouseEnter={(e) => { if (screen !== id) e.currentTarget.style.color = "#888"; }}
            onMouseLeave={(e) => { if (screen !== id) e.currentTarget.style.color = "#4a4a4a"; }}>
              <span style={{ fontSize: 14, width: 16, textAlign: "center" }}>{icon}</span>
              {label}
            </button>
          ))}
        </nav>

        <div style={{ padding: "14px 20px", borderTop: "1px solid #112214", color: "#1e3520", fontSize: 10, letterSpacing: 0.5 }}>
          ABIDAR v1.0
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, overflowY: "auto", padding: screen === "pos" ? 20 : "28px 32px" }}>
        <Screen />
      </main>
    </div>
  );
}
